import { useCallback, useContext, useEffect } from 'preact/hooks';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { ApplicationContext } from 'contexts/use-application-setup';
import { tokenStore } from 'global/token.store';
import { stateStore } from 'global/state.store';
import { INITIAL_PAGE_STATE } from 'constants/constants';
import { api } from '../api';

export const OAUTH: OrgInfo['ssoProtocol'] = 'OAUTH';
export const SAML: OrgInfo['ssoProtocol'] = 'SAML';
export const NEW_WINDOW_PARAMS =
  'height=700,width=700,status=yes,toolbar=no,menubar=no,location=no';
export const SSO_PATH_ERROR = 'No ssoPath is provided in the orgInfo';
export const POPUP_ERROR = 'Popup blocked! Please allow popups for this site.';
export const SSO_PROTOCOL_ERROR = 'Wrong ssoProtocol is provided in the orgInfo';
export const SSO_ERROR_TOAST_TEXT = 'SSO authentication error';

export const useSso = () => {
  const { orgInfo, thisSiteId, code } = useContext(ApplicationContext);
  const { showToast } = useContext(ToastServiceContext);

  const isSsoAllowed = orgInfo?.allows?.includes('SSO_LOGIN') ?? false;
  const ssoProtocol = orgInfo.ssoProtocol;
  const ssoPath = orgInfo.ssoPath;

  const ssoLogin = () => {
    if (!ssoPath) {
      console.error(SSO_PATH_ERROR);
      return;
    }
    if (!(ssoProtocol === OAUTH || ssoProtocol === SAML)) {
      console.error(SSO_PROTOCOL_ERROR);
      return;
    }

    const newWindow = window.open(
      buildSsoUrl({ ssoPath, ssoProtocol, code, thisSiteId }),
      '_blank',
      NEW_WINDOW_PARAMS,
    );
    if (!newWindow) {
      console.warn(POPUP_ERROR);
    }
  };

  const handleMessageEvent = useCallback((event: MessageEvent) => {
    if (event.data?.SOCIAL_LOGIN_SUCCESS) {
      const { authCode } = event.data.SOCIAL_LOGIN_SUCCESS;
      socialLoginSuccess(authCode);
    }
    if (event.data?.SOCIAL_LOGIN_FAILURE) {
      const { errorMessage, responseErrorType } = event.data.SOCIAL_LOGIN_FAILURE;
      console.warn('SSO authentication error', responseErrorType || errorMessage);
      showToast(ToastType.Error, SSO_ERROR_TOAST_TEXT);
    }
  }, []);

  const socialLoginSuccess = (authCode: string) => {
    const tempToken = `AuthCode ${authCode}`;
    const options = {
      headers: {
        Authorization: tempToken,
        'X-Sso-Request-Origin': 'WIDGET_ORIGIN',
      },
    };

    api.get(`${__API_BASE__}sso/v0/user/login/oauth-exchange`, undefined, options).then(
      (responseWithToken) => {
        tokenStore.setToken(responseWithToken.payload.accessToken);
        stateStore.state.next(INITIAL_PAGE_STATE);
      },
      (err) => {
        showToast(ToastType.Error, err.message);
      },
    );
  };

  useEffect(() => {
    // Waiting for a message from the SSO popup
    window.addEventListener('message', handleMessageEvent);

    return () => window.removeEventListener('message', handleMessageEvent);
  }, []);

  return {
    isSsoAllowed,
    ssoLogin,
  };
};

interface BuildSsoUrl {
  ssoProtocol: OrgInfo['ssoProtocol'];
  ssoPath: string;
  code: string;
  thisSiteId: string;
}

export const buildSsoUrl = (params: BuildSsoUrl): string => {
  const protocol = params.ssoProtocol.toLowerCase();
  const ssoUrl = `${__BASE_API_SSO__}sso/v0/public/login/${protocol}-init/${
    params.ssoPath
  }?${protocol}2_request_origin=WIDGET_ORIGIN&${protocol}2_redirect_uri=${encodeURIComponent(
    `${__BASE_API_SSO__}sso/oauth-logged-in?authCode=`,
  )}&${protocol}2_failure_redirect_uri=${encodeURIComponent(
    `${__BASE_API_SSO__}sso/oauth-failed?errorMessage=`,
  )}&${protocol}2_target_acc_code=${params.code}&${protocol}2_target_site_id=${params.thisSiteId}`;

  return ssoUrl;
};
